import React from 'react';
import { useGetMyInvitationsQuery } from '../store/api/localApi';
import { ScopedLink } from './ScopedLink';
import { Student } from '../types';
import { studentDisplayName } from '../utils/student';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface Props {
  student: Pick<Student, 'id' | 'firstName' | 'lastName' | 'registrationStatus'>;
}

/**
 * Full-width card that replaces every other piece of UI for a child whose
 * registration is still PENDING. The parent's only path forward is to fill in
 * the registration form. We resolve the form invitation for this child from
 * the parent's pending-invitation list — created by the auto-trigger when the
 * child was added — and link straight into FormFill.
 *
 * If for some reason no invitation exists (template not active when child was
 * created, or already expired), we explain and ask them to contact the
 * location. We don't try to create one from the parent UI.
 */
const RegistrationPendingCard: React.FC<Props> = ({ student }) => {
  const { data } = useGetMyInvitationsQuery();
  const invitation = data?.invitations?.find(
    (inv) => inv.studentId === student.id && inv.status === 'PENDING'
  );

  return (
    <div
      data-testid="registration-pending-card"
      className="rounded-lg border border-amber-200 bg-amber-50 p-6 sm:p-8 max-w-2xl mx-auto"
    >
      <div className="flex items-start gap-4">
        <ExclamationCircleIcon
          className="h-8 w-8 text-amber-600 flex-shrink-0"
          aria-hidden="true"
        />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-amber-900">
            Registration pending for {studentDisplayName(student)}
          </h2>
          <p className="mt-2 text-sm text-amber-900">
            Before you can see attendance, mark absences, or do anything else for this child, we
            need a few details. The location has sent you a registration form to fill in.
          </p>
          <div className="mt-5">
            {invitation ? (
              <ScopedLink
                to={`/forms/${invitation.formId}/fill?invitationId=${invitation.id}`}
                data-testid="registration-pending-cta"
                className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
              >
                Complete registration form
              </ScopedLink>
            ) : (
              <p className="text-sm text-amber-800 italic">
                We couldn't find a registration form for this child. Please contact the location to
                send you one.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPendingCard;
